import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Alert,
  FormGroup,
  Label,
  Input,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { serviceTypesService, ServiceType } from '../../../services/serviceTypesService';
import { individualsService } from '../../../services/individualsService';

interface Phase3ServicesFormProps {
  onSubmit: (values: any) => Promise<void>;
  onPrevious: () => void;
  initialValues: any;
  isSubmitting: boolean;
  individualId: string;
}

const Phase3ServicesForm: React.FC<Phase3ServicesFormProps> = ({
  onSubmit,
  onPrevious,
  initialValues,
  isSubmitting,
  individualId,
}) => {
  const { t } = useTranslation();
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('0');

  const loadServiceTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load service types
      const result = await serviceTypesService.getAll(true, false);
      setServiceTypes(result.serviceTypes);

      // Load existing services for this individual (if any)
      if (individualId) {
        try {
          const existingServices = await individualsService.getServices(individualId);
          const serviceTypeIds = existingServices.services.map(s => s.serviceTypeId);
          setSelectedServices(new Set(serviceTypeIds));
        } catch (err: any) {
          // It's OK if there are no services yet, just continue
          console.log('No existing services found or error loading them:', err);
        }
      }
    } catch (err: any) {
      setError(err.message || t('Erro ao carregar tipos de serviço'));
      console.error('Error loading service types:', err);
    } finally {
      setLoading(false);
    }
  }, [t, individualId]);

  useEffect(() => {
    loadServiceTypes();
  }, [loadServiceTypes]);

  // Helper function to find a service by ID in the tree
  const findServiceById = (services: ServiceType[], id: string): ServiceType | null => {
    for (const service of services) {
      if (service.id === id) return service;
      if (service.children && service.children.length > 0) {
        const found = findServiceById(service.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to get all descendant IDs
  const getAllDescendants = (service: ServiceType): string[] => {
    const descendants: string[] = [];

    const collectDescendants = (s: ServiceType) => {
      if (s.children && s.children.length > 0) {
        s.children.forEach(child => {
          descendants.push(child.id);
          collectDescendants(child);
        });
      }
    };

    collectDescendants(service);
    return descendants;
  };

  // Helper function to get all ancestor IDs
  const getAllAncestors = (services: ServiceType[], targetId: string): string[] => {
    const ancestors: string[] = [];

    const findAncestors = (services: ServiceType[], path: string[]): boolean => {
      for (const service of services) {
        const currentPath = [...path, service.id];

        if (service.id === targetId) {
          // Found the target, add all parents in the path (excluding the target itself)
          ancestors.push(...path);
          return true;
        }

        if (service.children && service.children.length > 0) {
          if (findAncestors(service.children, currentPath)) {
            return true;
          }
        }
      }
      return false;
    };

    findAncestors(services, []);
    return ancestors;
  };

  // Helper function to check if a parent has any selected children
  const hasSelectedChildren = (parentService: ServiceType, selectedSet: Set<string>): boolean => {
    if (!parentService.children || parentService.children.length === 0) {
      return false;
    }
    return parentService.children.some(child => selectedSet.has(child.id));
  };

  // Helper function to recursively uncheck ancestors if they have no other selected children
  const uncheckParentsIfNeeded = (
    services: ServiceType[],
    targetId: string,
    selectedSet: Set<string>
  ): void => {
    const ancestors = getAllAncestors(services, targetId);

    // Process ancestors from closest to farthest (reverse order)
    for (let i = ancestors.length - 1; i >= 0; i--) {
      const ancestorId = ancestors[i];
      const ancestorService = findServiceById(services, ancestorId);

      if (ancestorService && !hasSelectedChildren(ancestorService, selectedSet)) {
        selectedSet.delete(ancestorId);
      } else {
        // If this ancestor still has selected children, stop here
        // (parent ancestors should remain checked)
        break;
      }
    }
  };

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices((prev) => {
      const newSet = new Set(prev);

      // Find the service being toggled
      const service = findServiceById(serviceTypes, serviceId);
      if (!service) return prev;

      if (newSet.has(serviceId)) {
        // Unchecking - remove this service and all descendants
        newSet.delete(serviceId);
        const descendants = getAllDescendants(service);
        descendants.forEach(id => newSet.delete(id));

        // Uncheck ancestors if they have no other selected children
        uncheckParentsIfNeeded(serviceTypes, serviceId, newSet);
      } else {
        // Checking - add this service and all ancestors
        newSet.add(serviceId);
        const ancestors = getAllAncestors(serviceTypes, serviceId);
        ancestors.forEach(id => newSet.add(id));
      }

      return newSet;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For now, just complete without sending services
    // TODO: Send selected services to backend when AddIndividualServices endpoint is ready
    await onSubmit({
      selectedServices: Array.from(selectedServices),
    });
  };

  const renderServiceTree = (services: ServiceType[], level: number = 0): React.ReactElement => {
    return (
      <>
        {services.map((service) => (
          <div key={service.id} style={{ marginLeft: `${level * 20}px` }}>
            <FormGroup check className="mb-2">
              <Input
                type="checkbox"
                id={`service-${service.id}`}
                checked={selectedServices.has(service.id)}
                onChange={() => handleServiceToggle(service.id)}
              />
              <Label check for={`service-${service.id}`} className="mb-0">
                <strong>{service.name}</strong>
                {service.defaultPrice && (
                  <span className="text-muted ms-2">
                    ({t('R$')} {service.defaultPrice.toFixed(2)})
                  </span>
                )}
              </Label>
              {service.description && (
                <div className="text-muted small ms-4">{service.description}</div>
              )}
            </FormGroup>
            {service.children && service.children.length > 0 && (
              <div className="mt-2 mb-3">
                {renderServiceTree(service.children, level + 1)}
              </div>
            )}
          </div>
        ))}
      </>
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">{t('Seleção de Serviços')}</h5>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">{t('Carregando serviços disponíveis...')}</p>
            </div>
          ) : error ? (
            <Alert color="danger">
              <i className="ri-error-warning-line me-2"></i>
              {error}
              <Button
                color="link"
                size="sm"
                className="ms-2"
                onClick={loadServiceTypes}
              >
                {t('Tentar novamente')}
              </Button>
            </Alert>
          ) : serviceTypes.length === 0 ? (
            <Alert color="info">
              <i className="ri-information-line me-2"></i>
              {t('Nenhum serviço disponível no momento.')}
            </Alert>
          ) : (
            <>
              <Alert color="info" className="mb-3">
                <i className="ri-information-line me-2"></i>
                {t('Selecione os serviços que deseja contratar para esta pessoa física. Você poderá adicionar ou remover serviços depois.')}
              </Alert>

              {/* Tabs for service categories */}
              <Nav tabs className="mb-3">
                {serviceTypes.map((category, index) => (
                  <NavItem key={category.id}>
                    <NavLink
                      className={activeTab === index.toString() ? 'active' : ''}
                      onClick={() => setActiveTab(index.toString())}
                      style={{ cursor: 'pointer' }}
                    >
                      {category.name}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              {/* Tab content */}
              <TabContent activeTab={activeTab}>
                {serviceTypes.map((category, index) => (
                  <TabPane key={category.id} tabId={index.toString()}>
                    {activeTab === index.toString() && (
                      <div className="mb-3">
                        {category.children && category.children.length > 0 ? (
                          renderServiceTree(category.children, 0)
                        ) : (
                          <Alert color="info">
                            <i className="ri-information-line me-2"></i>
                            {t('Nenhum serviço disponível nesta categoria.')}
                          </Alert>
                        )}
                      </div>
                    )}
                  </TabPane>
                ))}
              </TabContent>

              {selectedServices.size > 0 && (() => {
                // Count only leaf services (services without children)
                const leafServiceCount = Array.from(selectedServices).filter(serviceId => {
                  const service = findServiceById(serviceTypes, serviceId);
                  return service && (!service.children || service.children.length === 0);
                }).length;

                return leafServiceCount > 0 ? (
                  <Alert color="success" className="mt-3">
                    <i className="ri-check-line me-2"></i>
                    {t('{{count}} serviço(s) selecionado(s)', { count: leafServiceCount })}
                  </Alert>
                ) : null;
              })()}

            </>
          )}
        </CardBody>
      </Card>

      <div className="d-flex justify-content-between gap-2 mt-4">
        <Button
          type="button"
          color="light"
          onClick={onPrevious}
          disabled={isSubmitting}
        >
          <i className="ri-arrow-left-line me-1"></i>
          {t('Anterior')}
        </Button>

        <Button
          type="submit"
          color="success"
          disabled={isSubmitting || loading}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {t('Finalizando...')}
            </>
          ) : (
            <>
              <i className="ri-check-line me-1"></i>
              {t('Finalizar Cadastro')}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default Phase3ServicesForm;
